import React from 'react';
import { COUNTRIES } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { useThemeColors } from '../../hooks/useThemeColors';
import type { FlowLookupState } from '../../hooks/useFlowLookup';
import { formatUtc } from '../../utils/dateWindow';
import {
  DIRECTION_LABELS,
  FLOW_DIRECTIONS,
  formatDistance,
  isFarSnap,
  roadTypeLabel,
} from '../../utils/flow';
import type { FlowOutcome, FlowRoad } from '../../utils/flow';
import DataStateNotice from './DataStateNotice';
import HourlyBarChart from './HourlyBarChart';

interface FlowResultPanelProps {
  lookup: FlowLookupState;
  outcome: FlowOutcome | null;
  country: CountryCode;
  dayLabel: string;
  onRetry(): void;
}

const fmt = (n: number) => n.toLocaleString('en-GB');
const roadName = (r: FlowRoad) => r.name ?? 'Unnamed road';

const Loading: React.FC = () => (
  <div role="status" className="space-y-3">
    <div className="skeleton h-5 w-2/3" />
    <div className="skeleton h-4 w-1/2" />
    <div className="skeleton h-48 w-full" />
    <p className="text-sm text-ink-muted">Looking up the nearest road and its traffic…</p>
    <p className="text-xs text-ink-muted">Clicking again cancels this lookup.</p>
  </div>
);

/** What the page shows for a lookup: idle hint, loading, error, no data, or the road and its chart. Presentational only. */
const FlowResultPanel: React.FC<FlowResultPanelProps> = ({ lookup, outcome, country, dayLabel, onRetry }) => {
  const colors = useThemeColors();
  if (lookup.status === 'idle') {
    return (
      <p className="text-sm text-ink-muted">
        Click a road on the map, or search for an address, to see the traffic on the nearest road.
      </p>
    );
  }
  if (lookup.status === 'loading') return <Loading />;

  if (lookup.status === 'error') {
    const retry = { label: 'Try again', onClick: onRetry };
    return lookup.kind === 'timeout' ? (
      <DataStateNotice
        tone="warn"
        title="This took too long"
        body="The lookup didn't finish within 90 seconds, so it was stopped. Try again, or pick another point."
        action={retry}
      />
    ) : (
      <DataStateNotice tone="error" title="Couldn't load traffic for this point" body={lookup.message} action={retry} />
    );
  }

  if (!outcome) return null;

  if (outcome.kind === 'empty') {
    const road = outcome.road;
    const nearest = road
      ? ` Nearest road: ${roadName(road)}${road.distanceM != null ? `, ${formatDistance(road.distanceM)} away` : ''}.`
      : '';
    return (
      <DataStateNotice
        tone="info"
        title="No traffic data here"
        body={`No car traffic recorded on roads near this point on ${dayLabel}. Try a main road or another day.${nearest}`}
      />
    );
  }

  const { road, slots, series, totals, total, duplicatesRemoved } = outcome;
  const typeLabel = roadTypeLabel(road.type);
  const meta = [
    typeLabel ? `Road type: ${typeLabel}` : null,
    road.distanceM != null ? `${formatDistance(road.distanceM)} from your point` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const hasGap = FLOW_DIRECTIONS.some((d) => series[d].some((v) => v === null));
  const colorOf = { 1: colors.series[0], [-1]: colors.series[1] } as const;
  const totalRows: Array<[string, number | null]> = [
    [DIRECTION_LABELS[1], totals[1]],
    [DIRECTION_LABELS[-1], totals[-1]],
    ['Both directions', total],
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink">{roadName(road)}</h2>
        {meta ? <p className="text-sm text-ink-muted">{meta}</p> : null}
      </div>

      {isFarSnap(road.distanceM) ? (
        <DataStateNotice
          tone="warn"
          title={`This road is ${formatDistance(road.distanceM as number)} from your point`}
          body="It is the nearest road with traffic data. Click closer to a road for a better match."
        />
      ) : null}

      <div>
        <h3 className="font-medium text-ink">People in cars per hour · {dayLabel}</h3>
        <p className="mb-2 text-xs text-ink-muted">
          Hours in UTC, from {formatUtc(slots[0], 'hourly')} to {formatUtc(slots[slots.length - 1], 'hourly')}.
        </p>
        <HourlyBarChart
          slots={slots}
          yLabel="People in cars"
          ariaLabel={`People in cars per hour on ${roadName(road)}, ${dayLabel}, by direction`}
          series={FLOW_DIRECTIONS.map((d) => ({
            key: d === 1 ? 'a' : 'b',
            label: DIRECTION_LABELS[d],
            values: series[d],
            color: colorOf[d],
          }))}
        />
      </div>

      <dl className="grid grid-cols-3 gap-2 text-sm">
        {totalRows.map(([label, n]) => (
          <div key={label} className="rounded-lg border border-paper-300 p-2">
            <dt className="text-xs text-ink-muted">{label}</dt>
            <dd className="font-semibold text-ink">{n == null ? 'No data' : `${fmt(n)} people`}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-ink-muted">
        Direction A and Direction B are the two directions of travel along this road. Which compass direction each one
        is has not been published yet.
      </p>
      {hasGap ? <p className="text-xs text-ink-muted">An hour without a bar has no published figure.</p> : null}

      {duplicatesRemoved > 0 ? (
        <DataStateNotice
          tone="info"
          title="Some hours were delivered twice"
          body={`${COUNTRIES[country].label} delivery days overlap around midnight, so ${duplicatesRemoved} hourly figures came back twice. The larger figure, from this day's delivery, is shown.`}
        />
      ) : null}
    </div>
  );
};

// Memoised: the page re-renders on every map pan, and a fresh series array would redraw the chart.
export default React.memo(FlowResultPanel);
